import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AiReviewStatus } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import { AiReview } from '~hooks/useAiReview/models';

import {
  AiReviewBadge,
  DismissedAiReviewBadge,
  DismissedAiReviewDetails,
  DismissedBanner,
  DismissFooter,
  getStatusColor,
} from '../LoadsTable/components/AiReviewIndicator';

interface AiReviewSummaryProps {
  aiReview: AiReview;
  isDismissing?: boolean;
  isUndoingDismiss?: boolean;
  onDismiss?: () => void;
  onUndoDismiss?: () => void;
}

function getStatusBackground(theme: Theme, status: AiReviewStatus) {
  switch (status) {
    case AiReviewStatus.APPROVED:
      return theme.semantic.statusSuccessBg;
    case AiReviewStatus.UNREADABLE:
      return theme.semantic.chipWarningBg;
    case AiReviewStatus.FLAGGED:
      return theme.semantic.statusErrorBg;
    default:
      throw new Error(`Unknown AI review status: ${status}`);
  }
}

export default function AiReviewSummary({
  aiReview,
  isDismissing = false,
  isUndoingDismiss = false,
  onDismiss,
  onUndoDismiss,
}: AiReviewSummaryProps) {
  const [expanded, setExpanded] = useState(false);

  if (aiReview.dismissedAt) {
    return (
      <DismissedAiReviewSummary
        aiReview={aiReview}
        isUndoing={isUndoingDismiss}
        onUndo={onUndoDismiss}
      />
    );
  }

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: getStatusBackground(theme, aiReview.status),
        borderRadius: theme.brandV2.borderRadius,
        p: theme.spacing(1, 1.5),
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiReviewBadge status={aiReview.status} />
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {t(`approvals.ai_review.status.${aiReview.status}`)}
            </Typography>
            <Typography
              variant="body2"
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                fontSize: '11px',
              })}
            >
              {t('approvals.ai_review.reviewed_by_ai')} · {aiReview.createdAt.fromNow()}
            </Typography>
          </Box>
        </Box>

        <PseudoLink
          title={expanded ? t('actions.hide_details') : t('actions.show_details')}
          action={() => setExpanded(!expanded)}
        />
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ my: 1.5 }} />
        <Box>
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: getStatusColor(theme, aiReview.status),
              mb: 1,
            })}
          >
            {t('approvals.ai_review.issues_found', { count: aiReview.issues.length })}
          </Typography>

          {aiReview.issues.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2, mb: 1.5 }}>
              {aiReview.issues.map((issue, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  component="li"
                  sx={(theme) => ({
                    color: theme.brandV2.colors.treadBlack,
                    lineHeight: 1.4,
                    listStyleType: 'square',
                  })}
                >
                  {issue}
                </Typography>
              ))}
            </Box>
          )}

          {aiReview.reasoning && (
            <Typography
              variant="body2"
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                lineHeight: 1.4,
              })}
            >
              {aiReview.reasoning}
            </Typography>
          )}

          {onDismiss && (
            <DismissFooter isDismissing={isDismissing} onDismiss={onDismiss} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

interface DismissedAiReviewSummaryProps {
  aiReview: AiReview;
  isUndoing: boolean;
  onUndo?: () => void;
}

function DismissedAiReviewSummary({
  aiReview,
  isUndoing,
  onUndo,
}: DismissedAiReviewSummaryProps) {
  return (
    <DismissedBanner
      badge={<DismissedAiReviewBadge sx={{ backgroundColor: 'transparent' }} />}
      dataTestId="ai-review-summary-dismissed"
    >
      <DismissedAiReviewDetails
        aiReview={aiReview}
        isUndoing={isUndoing}
        onUndo={onUndo}
      />
    </DismissedBanner>
  );
}
